import styled from 'styled-components';
import {
  ExpressionPostfixOnColumnName,
  IDisplayedMeasure,
  ILineMetaNewExpression,
  ILineMetaNewFunnel,
} from '../interfaces';
import { generateMeasureName } from '../CustomTooltip/utils';
import { FunnelModeKey } from '../../../utils/apollo/graphqlSchema';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { ITooltipProps } from '../hooks/tooltip/useSingleChartDataTooltip';
import { OtherLineName } from '../hooks/useOthersLine';
import BadgeRound from '../../BadgeRound';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';

interface IProps {
  showMeasureLabel: boolean;
  showGroupName: boolean;
  lineMeta: ILineMetaNewExpression[string] | ILineMetaNewFunnel[string] | null;
  query: IAnalysisResponse['query'];
  displayedMeasures: IDisplayedMeasure[];
  tooltipProps: ITooltipProps | null;
}

const MeasureContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: flex-start;
  align-items: center;
  width: -webkit-fill-available;
`;

const MeasureName = styled.div`
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupName = styled.div`
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: -webkit-fill-available;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const FrequencyMeasureLabel = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: flex-start;
  width: -webkit-fill-available;
  > div {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const TooltipLineLabel = ({
  showMeasureLabel,
  showGroupName,
  lineMeta,
  query,
  displayedMeasures,
  tooltipProps,
}: IProps) => {
  if (!tooltipProps) {
    return null;
  }
  const getMeasureName = () => {
    if (
      tooltipProps.line.name === OtherLineName.key ||
      tooltipProps.line.name ===
        OtherLineName.key + ExpressionPostfixOnColumnName.percentKey
    ) {
      return <MeasureName>Others</MeasureName>;
    }

    if (
      query?.__typename === 'FunnelQuery' &&
      (query.mode?.key === FunnelModeKey.Frequency ||
        query.mode?.key === FunnelModeKey.TimeToConvert)
    ) {
      assertCondition(lineMeta && 'funnelModeSlice' in lineMeta);
      const funnelModeSlice = lineMeta.funnelModeSlice;
      const sourceMeasure = displayedMeasures.find(
        (measure) => measure.key === funnelModeSlice?.firstMeasureKey,
      );
      const targetMeasure = displayedMeasures.find(
        (measure) => measure.key === funnelModeSlice?.lastMeasureKey,
      );
      assertNotNil(sourceMeasure);
      assertNotNil(targetMeasure);
      return (
        <FrequencyMeasureLabel>
          <MeasureContainer>
            <BadgeRound size="small">{sourceMeasure.label}</BadgeRound>
            <MeasureName>{sourceMeasure.name}</MeasureName>
          </MeasureContainer>
          <div>to</div>
          <MeasureContainer>
            <BadgeRound size="small">{targetMeasure.label}</BadgeRound>
            <MeasureName>{targetMeasure.name}</MeasureName>
          </MeasureContainer>
        </FrequencyMeasureLabel>
      );
    }

    return (
      <MeasureContainer>
        {showMeasureLabel && lineMeta?.measureLabel && (
          <BadgeRound size="small">{lineMeta?.measureLabel}</BadgeRound>
        )}
        {lineMeta && (
          <MeasureName>{generateMeasureName(lineMeta, undefined)}</MeasureName>
        )}
      </MeasureContainer>
    );
  };

  return (
    <LabelContainer>
      {getMeasureName()}
      {showGroupName && lineMeta && <GroupName>{lineMeta.sliceName}</GroupName>}
    </LabelContainer>
  );
};

export default TooltipLineLabel;
